import React, { useState, useRef, useEffect, useMemo } from 'react';

/**
 * BBoxOverlay component for displaying detection bounding boxes over images
 * Shows bounding box overlays over images for detection visualization
 */
const BBoxOverlay = ({ 
  src, 
  bbox, 
  bboxes, // Support multiple bounding boxes
  alt = '', 
  className = '', 
  overlayClassName = '',
  onLoad = null,
  onError = null,
  fillMode = 'contain', // 'contain' or 'cover'
  borderColor = null, // Optional custom border color (e.g., 'rgba(16,185,129,0.9)' or '#10b981')
  ...props 
}) => {
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [overlayStyle, setOverlayStyle] = useState({});
  const imgRef = useRef(null);
  // Parse bbox - can be object or array format
  const parseBbox = (bboxData) => {
    if (!bboxData) return null;
    
    // Handle nested bbox structure from database
    let actualBbox = bboxData;
    if (typeof bboxData === 'object' && 'bbox' in bboxData) {
      actualBbox = bboxData.bbox;
    }
    
    // Handle different bbox formats
    if (typeof actualBbox === 'object') {
      // Object format: {x1, y1, x2, y2} (pixel coordinates)
      if ('x1' in actualBbox && 'y1' in actualBbox && 'x2' in actualBbox && 'y2' in actualBbox) {
        return {
          x: parseFloat(actualBbox.x1),
          y: parseFloat(actualBbox.y1),
          width: parseFloat(actualBbox.x2 - actualBbox.x1),
          height: parseFloat(actualBbox.y2 - actualBbox.y1)
        };
      }
      // Object format: {x, y, width, height}
      else if ('x' in actualBbox && 'y' in actualBbox) {
        return {
          x: parseFloat(actualBbox.x),
          y: parseFloat(actualBbox.y),
          width: parseFloat(actualBbox.width || actualBbox.w),
          height: parseFloat(actualBbox.height || actualBbox.h)
        };
      }
      // Object format: {min_x, min_y, max_x, max_y}
      else if ('min_x' in actualBbox && 'min_y' in actualBbox) {
        return {
          x: parseFloat(actualBbox.min_x),
          y: parseFloat(actualBbox.min_y),
          width: parseFloat(actualBbox.max_x - actualBbox.min_x),
          height: parseFloat(actualBbox.max_y - actualBbox.min_y)
        };
      }
    } else if (Array.isArray(actualBbox) && actualBbox.length >= 4) {
      // Array format: [x, y, width, height] or [min_x, min_y, max_x, max_y]
      return {
        x: parseFloat(actualBbox[0]),
        y: parseFloat(actualBbox[1]),
        width: parseFloat(actualBbox[2] - actualBbox[0]),
        height: parseFloat(actualBbox[3] - actualBbox[1])
      };
    }
    
    return null;
  };

  // Normalize single bbox or multiple bboxes into a consistent array format
  const allBboxes = useMemo(() => {
    const bboxList = [];
    
    // Handle single bbox prop
    if (bbox) {
      const parsed = parseBbox(bbox);
      if (parsed) bboxList.push(parsed);
    }
    
    // Handle multiple bboxes prop
    if (bboxes && Array.isArray(bboxes)) {
      bboxes.forEach(bboxItem => {
        const parsed = parseBbox(bboxItem);
        if (parsed) bboxList.push(parsed);
      });
    }
    
    return bboxList;
  }, [bbox, bboxes]);
  
  // Recalculate overlay styles when image dimensions or bboxes change
  useEffect(() => {
    if (allBboxes.length === 0 || !imgRef.current || !imageDimensions.width || !imageDimensions.height) {
      setOverlayStyle({});
      return;
    }
    
    const img = imgRef.current;
    const containerWidth = img.clientWidth;
    const containerHeight = img.clientHeight;
    const naturalWidth = imageDimensions.width || img.naturalWidth;
    const naturalHeight = imageDimensions.height || img.naturalHeight;
    
    if (!naturalWidth || !naturalHeight) return;
    
    // Calculate how the image is scaled within the container
    const scaleX = containerWidth / naturalWidth;
    const scaleY = containerHeight / naturalHeight;
    
    let scale, renderedWidth, renderedHeight, offsetX, offsetY;
    
    if (fillMode === 'cover') {
      // object-cover uses the larger scale to fill the container
      scale = Math.max(scaleX, scaleY);
      renderedWidth = naturalWidth * scale;
      renderedHeight = naturalHeight * scale;
      
      // Calculate offsets to center the image within the container
      offsetX = (containerWidth - renderedWidth) / 2;
      offsetY = (containerHeight - renderedHeight) / 2;
    } else {
      // object-contain uses the smaller scale to fit within the container
      scale = Math.min(scaleX, scaleY);
      renderedWidth = naturalWidth * scale;
      renderedHeight = naturalHeight * scale;
      
      // Calculate offsets to center the image within the container
      offsetX = (containerWidth - renderedWidth) / 2;
      offsetY = (containerHeight - renderedHeight) / 2;
    }
    
    // Generate styles for all bounding boxes
    const overlayStyles = allBboxes.map((bboxData, index) => {
      // Convert bbox coordinates to container coordinates
      // Check if coordinates are normalized (0-1) or absolute pixels
      const isNormalized = bboxData.x <= 1 && bboxData.y <= 1 && bboxData.width <= 1 && bboxData.height <= 1;
      
      let left, top, width, height;
      if (isNormalized) {
        // Normalized coordinates: scale by natural image dimensions
        left = offsetX + (bboxData.x * naturalWidth * scale);
        top = offsetY + (bboxData.y * naturalHeight * scale);
        width = bboxData.width * naturalWidth * scale;
        height = bboxData.height * naturalHeight * scale;
      } else {
        // Absolute pixel coordinates: scale by container scale
        left = offsetX + (bboxData.x * scale);
        top = offsetY + (bboxData.y * scale);
        width = bboxData.width * scale;
        height = bboxData.height * scale;
      }
      
      // Assign a distinct color for each bbox (or use custom color if provided)
      const color = borderColor || (() => {
        const hue = (index * 137) % 360; // Spread colors evenly
        return `hsl(${hue}, 70%, 50%)`;
      })();
      
      return {
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        border: `2px solid ${color}`,
        borderRadius: '4px',
        pointerEvents: 'none',
        boxShadow: borderColor ? `0 0 15px ${color}59` : `0 0 0 1px ${color}40`,
        zIndex: 10
      };
    });
    
    setOverlayStyle(overlayStyles);
  }, [allBboxes, imageDimensions, src, fillMode, borderColor]);

  // Handle window resize to recalculate overlay
  useEffect(() => {
    const handleResize = () => {
      if (allBboxes.length === 0 || !imgRef.current || !imageDimensions.width || !imageDimensions.height) {
        setOverlayStyle({});
        return;
      }
      
      const img = imgRef.current;
      const containerWidth = img.clientWidth;
      const containerHeight = img.clientHeight;
      const naturalWidth = imageDimensions.width || img.naturalWidth;
      const naturalHeight = imageDimensions.height || img.naturalHeight;
      
      if (!naturalWidth || !naturalHeight) return;
      
      // Calculate how the image is scaled within the container
      const scaleX = containerWidth / naturalWidth;
      const scaleY = containerHeight / naturalHeight;
      
      let scale, renderedWidth, renderedHeight, offsetX, offsetY;
      
      if (fillMode === 'cover') {
        // object-cover uses the larger scale to fill the container
        scale = Math.max(scaleX, scaleY);
        renderedWidth = naturalWidth * scale;
        renderedHeight = naturalHeight * scale;
        
        // Calculate offsets to center the image within the container
        offsetX = (containerWidth - renderedWidth) / 2;
        offsetY = (containerHeight - renderedHeight) / 2;
      } else {
        // object-contain uses the smaller scale to fit within the container
        scale = Math.min(scaleX, scaleY);
        renderedWidth = naturalWidth * scale;
        renderedHeight = naturalHeight * scale;
        
        // Calculate offsets to center the image within the container
        offsetX = (containerWidth - renderedWidth) / 2;
        offsetY = (containerHeight - renderedHeight) / 2;
      }
      
      // Generate styles for all bounding boxes
      const overlayStyles = allBboxes.map((bboxData, index) => {
        // Convert bbox coordinates to container coordinates
        // Check if coordinates are normalized (0-1) or absolute pixels
        const isNormalized = bboxData.x <= 1 && bboxData.y <= 1 && bboxData.width <= 1 && bboxData.height <= 1;
        
        let left, top, width, height;
        if (isNormalized) {
          // Normalized coordinates: scale by natural image dimensions
          left = offsetX + (bboxData.x * naturalWidth * scale);
          top = offsetY + (bboxData.y * naturalHeight * scale);
          width = bboxData.width * naturalWidth * scale;
          height = bboxData.height * naturalHeight * scale;
        } else {
          // Absolute pixel coordinates: scale by container scale
          left = offsetX + (bboxData.x * scale);
          top = offsetY + (bboxData.y * scale);
          width = bboxData.width * scale;
          height = bboxData.height * scale;
        }
        
        // Assign a distinct color for each bbox (or use custom color if provided)
        const color = borderColor || (() => {
          const hue = (index * 137) % 360; // Spread colors evenly
          return `hsl(${hue}, 70%, 50%)`;
        })();
        
        return {
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${width}px`,
          height: `${height}px`,
          border: `2px solid ${color}`,
          borderRadius: '4px',
          pointerEvents: 'none',
          boxShadow: borderColor ? `0 0 15px ${color}59` : `0 0 0 1px ${color}40`,
          zIndex: 10
        };
      });
      
      setOverlayStyle(overlayStyles);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [allBboxes, imageDimensions, fillMode, borderColor]);
  
  if (allBboxes.length === 0 || !src) {
    // Fallback to regular image if no bbox
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onLoad={onLoad}
        onError={onError}
        {...props}
      />
    );
  }

  // Handle image load to get actual dimensions
  const handleImageLoad = (e) => {
    const img = e.target;
    const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
    setImageDimensions(dimensions);
    
    // Debug logging removed to prevent console spam
    
    if (onLoad) onLoad(e);
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`w-full h-full ${fillMode === 'cover' ? 'object-cover' : 'object-contain'} bg-gray-50`}
        onLoad={handleImageLoad}
        onError={onError}
        {...props}
      />
      {/* Render multiple bounding box overlays */}
      {Array.isArray(overlayStyle) ? (
        overlayStyle.map((style, index) => (
          <div 
            key={index}
            className={`${overlayClassName}`}
            style={style}
            aria-label={`Detection bounding box ${index + 1} for ${alt}`}
          />
        ))
      ) : (
        <div 
          className={`${overlayClassName}`}
          style={overlayStyle}
          aria-label={`Detection bounding box for ${alt}`}
        />
      )}
    </div>
  );
};

export default BBoxOverlay;
